import { Button } from '@shadcn/ui/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@shadcn/ui/components/ui/tooltip';
import { Popover, PopoverTrigger } from '@shadcn/ui/components/ui/popover';
import { cn } from '@shadcn/ui/lib/utils';
import { PenBoxIcon } from 'lucide-react';
import { FC, useState } from 'react';
import { QuickAddNotePopover } from './quick-add-note-popover';

interface QuickAddNoteButtonProps {
    contactId: string;
    onAddNote?: (e: React.MouseEvent) => void;
    onDropdownStateChange?: (isOpen: boolean) => void;
    variant?: 'default' | 'ghost' | 'outline';
    size?: 'sm' | 'icon-sm' | 'icon';
    className?: string;
    showLabel?: boolean;
}

export const QuickAddNoteButton: FC<QuickAddNoteButtonProps> = ({
    contactId,
    onAddNote,
    onDropdownStateChange,
    variant = 'ghost',
    size = 'icon-sm',
    className,
    showLabel = false,
}) => {
    const [isOpen, setIsOpen] = useState(false);

    const handleOpenChange = (open: boolean) => {
        setIsOpen(open);
        onDropdownStateChange?.(open);
    };

    const handleButtonClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        onAddNote?.(e);
        setIsOpen(true);
    };

    const handleNoteAdded = () => {
        // Optional: Add any additional logic when note is added
        console.log('Note added for contact:', contactId);
    };

    return (
        <Popover open={isOpen} onOpenChange={handleOpenChange}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <PopoverTrigger asChild>
                        <Button
                            variant={variant}
                            size={size}
                            onClick={handleButtonClick}
                            className={cn(
                                'data-[state=open]:bg-accent data-[state=open]:text-accent-foreground',
                                className
                            )}
                        >
                            <PenBoxIcon
                                className={cn(
                                    'h-4 w-4 flex-shrink-0',
                                    variant === 'outline' ? 'text-current' : 'text-gray-500'
                                )}
                            />
                            {showLabel && <span className="hidden lg:inline">Add note</span>}
                        </Button>
                    </PopoverTrigger>
                </TooltipTrigger>
                <TooltipContent>
                    <p>Add note</p>
                </TooltipContent>
            </Tooltip>
            <QuickAddNotePopover contactId={contactId} onClose={() => setIsOpen(false)} onNoteAdded={handleNoteAdded} />
        </Popover>
    );
};
